import type { AccountSummary } from "@finances/shared";

/**
 * Which accounts the dashboard's figures are about.
 *
 * It lives in the URL and nowhere else. The period picker remembers itself in
 * storage because every screen shows a period and they should agree; a focus
 * on two of five accounts is this screen's alone, and one silently restored a
 * week later is a dashboard quietly missing half the household's money.
 *
 * **Empty means every account**, the same convention a budget's `accountIds`
 * uses — so the ordinary case is the absent parameter and a clean URL.
 */

export function parseFocus(value: string | undefined): string[] {
  if (!value) return [];
  return value.split(",").filter((id) => id !== "");
}

/** Undefined rather than an empty string, so "all" leaves no parameter behind. */
export function focusParam(accountIds: string[]): string | undefined {
  return accountIds.length === 0 ? undefined : accountIds.join(",");
}

/**
 * The focus as a transactions filter, so a list opened from a figure asks for
 * the rows that figure counted. Absent rather than empty, so the ordinary case
 * sends nothing and keeps the cache key it always had.
 */
export function focusFilter(accountIds: string[]): { accountIds?: string[] } {
  return accountIds.length === 0 ? {} : { accountIds };
}

/**
 * The focus as a **Transactions screen** search, which filters one account at
 * a time and knows nothing of a list. A focus of one carries; a focus of
 * several cannot, so the link opens unfiltered by account rather than on a
 * filter that would silently mean something narrower than it says.
 */
export function focusLink(accountIds: string[]): { accountId?: string } {
  const [only] = accountIds;
  return accountIds.length === 1 && only ? { accountId: only } : {};
}

/**
 * The focus in words, for the picker's own trigger. One account is named,
 * because that is the case where the name is the useful fact; more are counted,
 * because three labels do not fit a control this wide.
 */
export function focusSummary(accountIds: string[], accounts: AccountSummary[]): string {
  if (accountIds.length === 0) return "All accounts";

  const [only] = accountIds;
  if (accountIds.length === 1) {
    return accounts.find((account) => account.id === only)?.label ?? "1 account";
  }
  return `${String(accountIds.length)} accounts`;
}
